import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Tag, TextInput } from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { MoneyInput } from '../components/MoneyInput';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

// Ban bot bi-a (founder 2026-10-19, task bida-ban-bot-cms, docs/specs/bida-ban-bot-cms.md): nhan vien co quyen bida.banBot dat
// cac muc cuoc co ban bot cho san trong sanh bi-a va so ban moi muc, rieng the thuc 8 / 9 bong. Muc khong sua rieng dung so ban
// chung (moiMuc, chu so huu dat o trang Bi-a). Quy bot chi doc o day: muc lon hon so du quy thi sanh an ban muc do.

type Hang = { cuoc: number; ban: { 8: number; 9: number }; rieng: boolean };
type Data = {
  cauHinh: { muc: Hang[]; moiMuc: number; quy: { bat: boolean; soDu: number | null; luc: number }; phien_ban: number } | null;
  loi?: string;
};
// dong tren form: so dang go (chu so), rieng = gui so ban cua dong nay (dong moi, dong da rieng, dong vua sua so ban)
type Dong = { k: number; cuoc: string; b8: string; b9: string; rieng: boolean };

const MUC_TOI_DA = 12;
const BAN_TOI_DA = 20;
const CUOC_TOI_DA = 1e13;
const chuSo = (s: string, n: number) => s.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, n);
let khoaMoi = 0;
const tuHang = (h: Hang): Dong => ({ k: ++khoaMoi, cuoc: String(h.cuoc), b8: String(h.ban[8]), b9: String(h.ban[9]), rieng: h.rieng });
const gui = (ds: Dong[]) => [...ds].sort((x, y) => Number(x.cuoc) - Number(y.cuoc))
  .map((d) => (d.rieng ? { cuoc: Number(d.cuoc), ban: { 8: Number(d.b8), 9: Number(d.b9) } } : { cuoc: Number(d.cuoc) }));

function loiForm(ds: Dong[]): string | null {
  if (ds.length > MUC_TOI_DA) return 'Tối đa ' + MUC_TOI_DA + ' mức cược';
  for (const d of ds) {
    const c = Number(d.cuoc);
    if (d.cuoc === '' || c < 1 || c > CUOC_TOI_DA) return 'Mỗi dòng cần một mức cược từ 1';
    if (d.b8 === '' || d.b9 === '' || Number(d.b8) > BAN_TOI_DA || Number(d.b9) > BAN_TOI_DA) return 'Số bàn mỗi thể thức từ 0 đến ' + BAN_TOI_DA;
  }
  if (new Set(ds.map((d) => Number(d.cuoc))).size !== ds.length) return 'Mỗi mức cược chỉ một dòng';
  return null;
}

export function BidaBanBotPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['bida-ban-bot'], queryFn: () => api.get<Data>('/api/admin/games/bida/ban-bot'), refetchInterval: 15000 });
  const [form, setForm] = useState<{ phien: number; ds: Dong[] } | null>(null);
  const [xong, setXong] = useState<Command | null>(null);
  const luu = useMutation({
    mutationFn: (b: { phien_ban: number; muc: ReturnType<typeof gui> }) => api.put<{ command: Command }>('/api/admin/games/bida/ban-bot', b),
    onSuccess: (r) => { setXong(r.command); setForm(null); qc.invalidateQueries({ queryKey: ['bida-ban-bot'] }); },
  });

  const header = <PageHeader title="Bàn bot bi-a" lead="Mức cược có bàn bot chờ sẵn trong sảnh bi-a và số bàn mỗi mức." />;
  if (q.isLoading) return <div className="page">{header}<BlockSkeleton lines={6} /></div>;
  if (q.error || !q.data) return <div className="page">{header}<ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const ch = q.data.cauHinh;
  if (!ch) {
    return (
      <div className="page">
        {header}
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Kết nối máy game" subtitle="Hệ A chưa trả lời: chưa đọc được bàn bot bi-a." />
      </div>
    );
  }
  // form giu phien ban luc bat dau sua: luu gui dung phien ban do (may chu tu choi neu cai dat vua doi o noi khac)
  const goc = ch.muc.map(tuHang);
  const f = form ?? { phien: ch.phien_ban, ds: goc };
  const doi = JSON.stringify(gui(f.ds)) !== JSON.stringify(gui(goc)) || f.phien !== ch.phien_ban;
  const loi = loiForm(f.ds);
  const datDs = (ds: Dong[]) => setForm({ phien: f.phien, ds });
  const sua = (k: number, x: Partial<Dong>) => datDs(f.ds.map((d) => (d.k === k ? { ...d, ...x } : d)));
  const quy = ch.quy;
  const trangThai = (d: Dong) => {
    if (d.cuoc === '') return null;
    if (!quy.bat) return <Tag type="gray" size="sm">Bot tắt</Tag>;
    return quy.soDu != null && quy.soDu >= Number(d.cuoc)
      ? <Tag type="green" size="sm">Quỹ đủ</Tag>
      : <Tag type="red" size="sm">Quỹ không đủ: bàn ẩn</Tag>;
  };

  return (
    <div className="page">
      {header}
      {xong ? (
        <div>
          <InlineNotification kind={xong.status === 'SUCCEEDED' ? 'success' : 'warning'} lowContrast onClose={() => { setXong(null); return false; }}
            title={xong.status === 'SUCCEEDED' ? 'Đã lưu bàn bot' : 'Lệnh lưu chưa xong'}
            subtitle={xong.status === 'SUCCEEDED' ? 'Sảnh bi-a đổi theo trong vòng một phút.'
              : (HE_A_ERRORS[xong.lastError ?? ''] ?? (xong.lastError || xong.status))} />
          <p><Link className="cell-link" to={'/commands/' + xong.id}>Xem lệnh</Link></p>
        </div>
      ) : null}
      <section className="card section">
        {quy.bat ? (
          <p style={{ margin: 0 }}>
            Quỹ bot: <strong>{quy.soDu != null ? formatVnd(quy.soDu) : 'chưa đọc được'}</strong>
            <span className="muted"> (đọc lúc {new Date(quy.luc).toLocaleTimeString('vi-VN')}). Mức lớn hơn số dư quỹ thì sảnh ẩn bàn mức đó; máy game kiểm lại số dư lúc khách vào bàn.</span>
          </p>
        ) : (
          <InlineNotification kind="warning" lowContrast hideCloseButton title="Bot hoặc quỹ bot đang tắt"
            subtitle="Sảnh bi-a không có bàn bot nào cho tới khi chủ sở hữu bật cả hai ở trang Bi-a." />
        )}
      </section>
      <form aria-label="Bàn bot bi-a" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}
        onSubmit={(e) => { e.preventDefault(); if (doi && !loi) luu.mutate({ phien_ban: f.phien, muc: gui(f.ds) }); }}>
        {f.ds.length ? (
          <table className="plain">
            <thead><tr><th>Mức cược</th><th>Bàn 8 bóng</th><th>Bàn 9 bóng</th><th>Quỹ</th><th aria-label="Xóa" /></tr></thead>
            <tbody>
              {f.ds.map((d) => (
                <tr key={d.k}>
                  <td data-label="Mức cược">
                    <MoneyInput id={'bbb-cuoc-' + d.k} labelText="Mức cược" hideLabel value={d.cuoc} maxDigits={14}
                      onChange={(v) => sua(d.k, { cuoc: v })} />
                  </td>
                  <td data-label="Bàn 8 bóng">
                    <TextInput id={'bbb-8-' + d.k} labelText="Bàn 8 bóng" hideLabel inputMode="numeric" value={d.b8}
                      onChange={(e) => sua(d.k, { b8: chuSo(e.target.value, 2), rieng: true })} />
                  </td>
                  <td data-label="Bàn 9 bóng">
                    <TextInput id={'bbb-9-' + d.k} labelText="Bàn 9 bóng" hideLabel inputMode="numeric" value={d.b9}
                      onChange={(e) => sua(d.k, { b9: chuSo(e.target.value, 2), rieng: true })} />
                  </td>
                  <td data-label="Quỹ">{trangThai(d)}{d.rieng ? null : <span className="muted"> số chung</span>}</td>
                  <td>
                    <Button kind="ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Bỏ mức này" tooltipPosition="left"
                      onClick={() => datDs(f.ds.filter((x) => x.k !== d.k))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="muted">Chưa có mức nào: sảnh bi-a không có bàn bot chờ sẵn.</p>}
        <div className="row">
          <Button kind="tertiary" size="md" renderIcon={Add} disabled={f.ds.length >= MUC_TOI_DA}
            onClick={() => datDs([...f.ds, { k: ++khoaMoi, cuoc: '', b8: String(ch.moiMuc), b9: String(ch.moiMuc), rieng: true }])}>
            Thêm mức
          </Button>
          <Button type="submit" size="md" disabled={!doi || !!loi || luu.isPending}>{luu.isPending ? 'Đang lưu' : 'Lưu'}</Button>
          {form ? <Button kind="ghost" size="md" onClick={() => setForm(null)}>Hủy sửa</Button> : null}
        </div>
        {doi && loi ? <p className="muted" role="alert">{loi}</p> : null}
        <InlineError error={luu.error} />
        <p className="muted">
          Số bàn là số bàn bot chờ sẵn của mỗi thể thức ở mức đó (0 đến {BAN_TOI_DA}); khách vào một bàn thì bàn mới thay vào. Dòng
          "số chung" dùng số bàn chung {ch.moiMuc} của trang Bi-a cho tới khi sửa số ở dòng đó. Tối đa {MUC_TOI_DA} mức. Trang này
          không đổi quỹ bot.
        </p>
      </form>
    </div>
  );
}
